<template>
    <section class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm tw-overflow-hidden xl:tw-col-span-7">
        <div class="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-px-4 tw-pt-4 tw-mb-3">
            <div>
                <div class="tw-text-xs tw-font-bold tw-uppercase tw-text-gray-700">
                    {{ $t("assignDriver.searchAvailablePackages") }}
                    <span class="tw-text-xs tw-font-normal tw-normal-case tw-text-gray-400">
                        ({{ $t("assignDriver.filterToAssign") }})
                    </span>
                </div>
            </div>
            <button
                class="tw-text-xs tw-font-bold tw-text-blue-600"
                type="button"
                @click.prevent="$emit('clear-filters')"
            >
                {{ $t("assignDriver.resetFilters") }}
            </button>
        </div>

        <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3 tw-px-4 tw-mb-3">
            <div>
                <label class="tw-block tw-text-xs tw-font-bold tw-text-gray-600 tw-mb-2">
                    {{ $t("assignDriver.bookingNumber") }}
                </label>
                <a-input
                    :value="searchBox"
                    class="tw-w-full"
                    :placeholder="$t('assignDriver.bookingPlaceholder')"
                    @input="$emit('update-search-box', $event.target ? $event.target.value : $event)"
                    @pressEnter="$emit('search-packages')"
                >
                    <a-icon slot="prefix" type="search" />
                </a-input>
            </div>

            <div>
                <label class="tw-block tw-text-xs tw-font-bold tw-text-gray-600 tw-mb-2">
                    {{ $t("assignDriver.zone") }}
                </label>
                <a-select
                    :value="zoneId"
                    show-search
                    allow-clear
                    class="tw-w-full"
                    :default-active-first-option="false"
                    :filter-option="false"
                    :not-found-content="null"
                    :placeholder="$t('assignDriver.selectZone')"
                    @search="$emit('search-zone', $event)"
                    @change="$emit('change-zone', $event)"
                >
                    <a-select-option
                        v-for="zone in zoneList"
                        :key="zone.zone_id"
                        :value="zone.zone_id"
                    >
                        {{ zone.zone_name }}
                    </a-select-option>
                </a-select>
            </div>

            <div class="md:tw-col-span-2">
                <label class="tw-block tw-text-xs tw-font-bold tw-text-gray-600 tw-mb-2">
                    {{ $t("assignDriver.receiverPhone") }}
                </label>
                <div class="tw-flex tw-items-center tw-gap-2">
                    <a-input
                        :value="receiverPhone"
                        class="tw-flex-1"
                        :placeholder="$t('assignDriver.searchPhoneNumber')"
                        @input="$emit('update-receiver-phone', $event.target ? $event.target.value : $event)"
                        @pressEnter="$emit('search-receiver-phone')"
                    >
                        <a-icon slot="prefix" type="phone" />
                    </a-input>
                    <ts-button color="primary" @click.prevent="$emit('search-packages')">
                        <i class="fa fa-search tw-mr-1"></i>
                        {{ $t("assignDriver.filter") }}
                    </ts-button>
                    <button
                        class="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded tw-border tw-border-gray-300 tw-bg-gray-50 tw-text-gray-500"
                        type="button"
                        @click.prevent="$emit('clear-filters')"
                    >
                        <i class="fa fa-times"></i>
                    </button>
                </div>
            </div>
        </div>

        <div class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-gray-100 tw-px-4 tw-py-3">
            <div class="tw-flex tw-items-center tw-gap-2">
                <div class="tw-text-sm tw-font-bold tw-uppercase tw-text-gray-800">
                    {{ $t("assignDriver.availablePackages") }}
                </div>
                <span class="tw-inline-flex tw-h-5 tw-min-w-8 tw-items-center tw-justify-center tw-rounded tw-bg-blue-50 tw-px-2 tw-text-xs tw-font-bold tw-text-blue-700">
                    {{ rows.length }}
                </span>
            </div>
            <button
                class="tw-inline-flex tw-h-8 tw-items-center tw-gap-2 tw-rounded tw-border tw-border-blue-200 tw-bg-blue-50 tw-px-3 tw-text-xs tw-font-bold tw-text-blue-600 disabled:tw-opacity-50"
                type="button"
                :disabled="!rows.length"
                @click.prevent="$emit('select-all')"
            >
                <i class="fa fa-plus"></i>
                {{ $t("assignDriver.selectAllMatching") }}
            </button>
        </div>

        <div class="available-package-scroll tw-overflow-y-auto tw-border-t tw-border-gray-100">
            <table class="tw-w-full tw-min-w-full tw-border-collapse">
                <thead>
                    <tr>
                        <th class="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-text-center tw-w-10 tw-px-3 tw-py-3">
                            <a-checkbox disabled />
                        </th>
                        <th class="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-text-left tw-text-xs tw-font-bold tw-uppercase tw-text-gray-500 tw-px-3 tw-py-3">{{ $t("assignDriver.packageInfo") }}</th>
                        <th class="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-text-left tw-text-xs tw-font-bold tw-uppercase tw-text-gray-500 tw-px-3 tw-py-3">{{ $t("assignDriver.receiverPhone") }}</th>
                        <th class="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-text-left tw-text-xs tw-font-bold tw-uppercase tw-text-gray-500 tw-px-3 tw-py-3">{{ $t("assignDriver.location") }}</th>
                        <th class="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-text-right tw-text-xs tw-font-bold tw-uppercase tw-text-gray-500 tw-px-3 tw-py-3">{{ $t("assignDriver.amountPackages") }}</th>
                        <th class="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-text-center tw-text-xs tw-font-bold tw-uppercase tw-text-gray-500 tw-px-3 tw-py-3">{{ $t("assignDriver.action") }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="record in rows"
                        :key="record.booking_id"
                        class="tw-bg-white hover:tw-bg-blue-50"
                    >
                        <td class="tw-border-b tw-border-gray-100 tw-text-center tw-px-3 tw-py-3">
                            <a-checkbox :checked="isSelected(record)" @change="$emit('add', record)" />
                        </td>
                        <td class="tw-border-b tw-border-gray-100 tw-px-3 tw-py-3 tw-whitespace-nowrap">
                            <div class="tw-text-xs tw-font-bold tw-text-blue-600">{{ record.booking_number }}</div>
                            <div class="tw-text-xs tw-font-bold tw-text-gray-800 tw-truncate tw-max-w-xs">{{ record.shop_name || '-' }}</div>
                        </td>
                        <td class="tw-border-b tw-border-gray-100 tw-px-3 tw-py-3 tw-whitespace-nowrap">
                            <div class="tw-text-xs tw-font-bold tw-text-gray-800">{{ record.receiver_phone || '-' }}</div>
                        </td>
                        <td class="tw-border-b tw-border-gray-100 tw-px-3 tw-py-3">
                            <div class="tw-text-xs tw-text-gray-700 tw-truncate tw-max-w-sm">{{ record.destination || '-' }}</div>
                        </td>
                        <td class="tw-border-b tw-border-gray-100 tw-px-3 tw-py-3 tw-text-right tw-whitespace-nowrap">
                            <div class="tw-text-xs tw-font-bold tw-text-gray-900">{{ formatAmount(record.purchase_amount) }}</div>
                            <div class="tw-text-xs tw-text-gray-500">{{ record.number_of_package || 0 }} {{ $t("assignDriver.packages") }}</div>
                        </td>
                        <td class="tw-border-b tw-border-gray-100 tw-text-center tw-px-3 tw-py-3">
                            <button
                                class="tw-inline-flex tw-h-7 tw-items-center tw-gap-1 tw-rounded tw-border tw-border-blue-200 tw-bg-blue-50 tw-px-2 tw-text-xs tw-font-bold tw-text-blue-600 disabled:tw-opacity-50"
                                type="button"
                                :disabled="isSelected(record)"
                                @click.prevent="$emit('add', record)"
                            >
                                <i class="fa fa-plus"></i>
                                {{ $t("assignDriver.add") }}
                            </button>
                        </td>
                    </tr>
                    <tr v-if="!rows.length">
                        <td colspan="6" class="tw-h-56 tw-text-center">
                            <div class="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-py-10">
                                <div class="tw-relative tw-mb-4 tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-blue-100 tw-bg-blue-50 tw-text-blue-600">
                                    <i class="fa fa-cube tw-text-2xl"></i>
                                    <span class="tw-absolute tw--right-1 tw--bottom-1 tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-blue-100 tw-bg-white tw-text-blue-500">
                                        <i class="fa fa-search tw-text-xs"></i>
                                    </span>
                                </div>
                                <div class="tw-text-sm tw-font-bold tw-text-gray-900">
                                    {{ $t("assignDriver.noAvailablePackages") }}
                                </div>
                                <div class="tw-mt-2 tw-max-w-md tw-text-xs tw-leading-5 tw-text-gray-500">
                                    {{ $t("assignDriver.noAvailablePackagesHint") }}
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </section>
</template>

<script>
export default {
    name: "AvailablePackagesPanel",
    props: {
        rows: {
            type: Array,
            default: () => []
        },
        selectedIds: {
            type: Array,
            default: () => []
        },
        searchBox: {
            default: null
        },
        receiverPhone: {
            default: null
        },
        zoneId: {
            default: null
        },
        zoneList: {
            type: Array,
            default: () => []
        }
    },
    methods: {
        isSelected(record) {
            return this.selectedIds.some(id => id == record.booking_id);
        },
        formatAmount(value) {
            const amount = Number(value || 0);

            return `$ ${amount.toFixed(2)}`;
        }
    }
};
</script>
